import { useState } from "react";
import {
  Share2,
  Check,
  X,
  FileText,
  SlidersHorizontal,
  GitFork,
  Building2,
} from "lucide-react";
import { acceptShare, declineShare } from "../../../../../api/sharing";
import FeedShareCard from "./FeedShareCard";
import GraphShareCard from "./GraphShareCard";
import ReportShareCard from "./ReportShareCard";
import WikiShareCard from "./WikiShareCard";

const CONTENT_TYPE_CONFIG = {
  custom_feed_view: {
    label: "Feed View",
    icon: SlidersHorizontal,
    Preview: FeedShareCard,
  },
  custom_graph_view: {
    label: "Graph View",
    icon: GitFork,
    Preview: GraphShareCard,
  },
  report: { label: "Report", icon: FileText, Preview: ReportShareCard },
  wiki_case: { label: "Wiki Case", icon: Building2, Preview: WikiShareCard },
};

const ShareInviteCard = ({ msg, userUuid, onClick }) => {
  const [status, setStatus] = useState(() => {
    if (msg.accepted_by?.includes(userUuid)) return "accepted";
    if (msg.declined_by?.includes(userUuid)) return "declined";
    return "pending";
  });
  const [loading, setLoading] = useState(false);

  const meta = msg.share_meta || {};
  const preview = meta.preview || {};
  const isOwner = userUuid === meta.owner_uuid;
  const contentType = meta.content_type || "";
  const config =
    CONTENT_TYPE_CONFIG[contentType] || CONTENT_TYPE_CONFIG.custom_feed_view;
  const IconComponent = config.icon;
  const PreviewComponent = config.Preview;
  const itemName = preview.item_name || preview.company_name || "Shared item";
  const iconColor = preview.item_color || "rgba(14,165,233,0.85)";

  const handleAccept = async () => {
    setLoading(true);
    try {
      await acceptShare(meta.invite_id, meta.content_type, meta.document_id);
      setStatus("accepted");
    } catch (e) {
      console.error("Accept share failed:", e);
    }
    setLoading(false);
  };

  const handleDecline = async () => {
    setLoading(true);
    try {
      await declineShare(meta.invite_id, meta.content_type, meta.document_id);
      setStatus("declined");
    } catch (e) {
      console.error("Decline share failed:", e);
    }
    setLoading(false);
  };

  if (meta.target_type === "general" && status === "accepted") return null;

  return (
    <div
      className="rounded-xl border border-surfaceBorder bg-dropdownBg/60 backdrop-blur-sm overflow-hidden max-w-sm cursor-pointer hover:bg-btnHover/40 transition-colors"
      onClick={() => onClick?.(msg)}
    >
      <div className="flex items-center gap-x-2.5 px-3.5 pt-3 pb-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: iconColor + "20" }}
        >
          <IconComponent
            className="h-4 w-4"
            strokeWidth={1.5}
            style={{ color: iconColor }}
          />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-light text-mainText truncate">
            {itemName}
          </p>
          <p className="text-[11px] font-light text-mutedText/60">
            {config.label} · Shared by {meta.owner_name || "Unknown"}
          </p>
        </div>
      </div>
      <div className="px-3.5 pb-2">
        <PreviewComponent preview={preview} />
      </div>
      {msg.content && (
        <div className="px-3.5 pb-2">
          <p className="text-[12px] font-light text-mutedText italic">
            "{msg.content}"
          </p>
        </div>
      )}
      <div className="px-3.5 pb-3 pt-1">
        {isOwner ? (
          <p className="text-[11px] font-light text-mutedText/60 flex items-center gap-x-1.5">
            <Share2 className="h-3 w-3" strokeWidth={2} />
            You shared this
          </p>
        ) : status === "accepted" ? (
          <p className="text-[11px] font-light text-activeCase flex items-center gap-x-1.5">
            <Check className="h-3 w-3" strokeWidth={2} />
            You accepted this share
          </p>
        ) : status === "declined" ? (
          <p className="text-[11px] font-light text-mutedText/60 flex items-center gap-x-1.5">
            <X className="h-3 w-3" strokeWidth={2} />
            You declined this share
          </p>
        ) : (
          <div
            className="flex items-center gap-x-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleAccept}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-x-1.5 px-3 py-1.5 rounded-lg text-[12px] font-light bg-velBlue/15 text-velBlue hover:bg-velBlue/25 transition-colors cursor-pointer disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" strokeWidth={2} />
              {meta.target_type === "general" ? "Join" : "Accept"}
            </button>
            <button
              onClick={handleDecline}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-x-1.5 px-3 py-1.5 rounded-lg text-[12px] font-light bg-surface text-mutedText hover:bg-btnHover transition-colors cursor-pointer disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
              Decline
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareInviteCard;
